import { useState } from 'react'

import { SplitSupplyForm } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  FieldRequiredIndicator,
  FieldRoot,
  HStack,
  Input,
  SecondaryButton,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { DsSelect } from '~/components/common/DsSelect'
import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
} from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { ToggleButton } from '~/components/common/ToggleButton'
import { convertAreaLabel } from '~/models/area'
import { BasePatternsSection } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/BasePatternsSection'
import {
  type BusinessOption,
  type SplitSupplyFormOptions,
  businessLabel,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/formOptions'
import { supplyFormLabel } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/labels'
import { type SplitSupplyFormValues } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/schema'
import { applySelfRole } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/selfRole'
import {
  SLOT_END_OPTIONS,
  SLOT_START_OPTIONS,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/slotTime'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { voltageClassLabel } from '~/pages/AdminOcctoMaster/tabs/_common/models/voltageClass'

const NS = 'page:adminOcctoMaster.splitSupplyContract' as const
const AREA_ALL = 'all'

const SUPPLY_FORMS = [
  SplitSupplyForm.FIXED,
  SplitSupplyForm.TIME_SPLIT,
  SplitSupplyForm.VARIABLE,
] as const

type Side = 'loadFollowing' | 'base'

/** 「通告値を出す側」の選択肢 (自社 / 他社。 保存は自社の立場との組み合わせでベース分 / 負荷追随分)。 */
const NOTICE_BY_CHOICES = ['self', 'other'] as const

type Props = {
  mode: 'create' | 'edit'
  control: Control<SplitSupplyFormValues>
  errors: FieldErrors<SplitSupplyFormValues>
  setValue: UseFormSetValue<SplitSupplyFormValues>
  getValues: UseFormGetValues<SplitSupplyFormValues>
  options: SplitSupplyFormOptions
  isLoadingFormOptions: boolean
  /** 候補の取得に失敗した (候補 0 件とは別の状態)。 */
  isFormOptionsError: boolean
  /** 候補の取得をやり直す。 */
  onRetryFormOptions: () => void
  /** 編集: 保存済みの両側の事業者 (候補に無くても「候補外」として表示するため)。 */
  savedBusinesses: BusinessOption[]
  /** 編集: 顧客管理の需要家名 (無ければ undefined)。 */
  customerName: string | undefined
}

/**
 * 分割供給契約の Create / Edit 共通フォーム。
 *
 * 自社の立場を選ぶと自社の側に自社の事業者が入る (1 者なら自動、 複数なら選ぶ)。
 * 他社の側は backend が絞った候補から選ぶ。 立場の導出・候補・適格性の判断は
 * backend で、 ここは入力を送るだけ (候補外の既存値は「候補外」として表示したまま残せる)。
 */
export const SplitSupplyContractFormFields = ({
  mode,
  control,
  errors,
  setValue,
  getValues,
  options,
  isLoadingFormOptions,
  isFormOptionsError,
  onRetryFormOptions,
  savedBusinesses,
  customerName,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const selfRole = useWatch({ control, name: 'selfRole' })
  const supplyForm = useWatch({ control, name: 'supplyForm' })
  const noticeBy = useWatch({ control, name: 'noticeBy' })
  const slotFrom = useWatch({ control, name: 'ownSlotFrom' })
  const patterns = useWatch({ control, name: 'basePatterns' })
  const idPrefix = mode === 'edit' ? 'splitSupply-edit' : 'splitSupply'
  // 新規登録の供給地点をエリアで絞る (画面だけの絞り込みで、 保存される値は変わらない)
  const [areaFilter, setAreaFilter] = useState<string>(AREA_ALL)
  const areaOptions = [...new Set(options.supplyPoints.map((p) => p.areaCode))]
    .sort((a, b) => a - b)
    .map((area) => ({ value: String(area), label: convertAreaLabel(area) }))
  const visibleSupplyPoints = options.supplyPoints.filter(
    (p) => areaFilter === AREA_ALL || String(p.areaCode) === areaFilter,
  )

  const knownBusinesses = [
    ...options.ownBusinesses,
    ...options.partnerBusinesses,
    ...savedBusinesses,
  ]
  const outOfCandidateLabel = (id: string): string | undefined => {
    const found = knownBusinesses.find((b) => b.id === id)
    if (found === undefined) return undefined
    // 候補を取得している間・取得に失敗した間は、 候補に無いのか分からないので「候補外」とは出さない
    if (isLoadingFormOptions || isFormOptionsError) return businessLabel(found)
    return t(`${NS}.form.outOfCandidate`, {
      name: found.name,
      code: found.code,
    })
  }
  const toOptions = (
    list: BusinessOption[],
  ): Array<{ value: string; label: string }> =>
    list.map((b) => ({ value: b.id, label: businessLabel(b) }))

  const selectRole = (role: Side): void => {
    const next = applySelfRole(
      getValues(),
      role,
      options.ownBusinesses.map((b) => b.id),
    )
    setValue('selfRole', role, { shouldDirty: true })
    setValue(
      'loadFollowingBusinessMasterId',
      next.loadFollowingBusinessMasterId,
      { shouldDirty: true },
    )
    setValue('baseBusinessMasterId', next.baseBusinessMasterId, {
      shouldDirty: true,
    })
  }

  const businessField = (side: Side): React.ReactElement => {
    const fieldName =
      side === 'loadFollowing'
        ? 'loadFollowingBusinessMasterId'
        : 'baseBusinessMasterId'
    const isOwn = selfRole === side
    const label = t(`${NS}.form.business`)
    const error = errors[fieldName]
    const ownOnly =
      isOwn && options.ownBusinesses.length === 1
        ? options.ownBusinesses[0]
        : undefined
    return (
      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={error !== undefined}
            required
          >
            <FieldLabel htmlFor={`${idPrefix}-${fieldName}`}>
              {label}
              <FieldRequiredIndicator />
            </FieldLabel>
            {ownOnly !== undefined && field.value === ownOnly.id ? (
              // 自社の事業者が 1 者: 自動で入り、 入力欄ではなく文で表示する
              <Text id={`${idPrefix}-${fieldName}`} paddingY={2}>
                {businessLabel(ownOnly)}
              </Text>
            ) : (
              <FormSelect
                width="100%"
                aria-label={label}
                control={control}
                fallbackLabel={outOfCandidateLabel}
                isDisabled={selfRole === ''}
                name={fieldName}
                options={toOptions(
                  isOwn ? options.ownBusinesses : options.partnerBusinesses,
                )}
                placeholder={t('common:form.selectPlaceholder')}
              />
            )}
            {selfRole === '' && (
              <FieldHelperText>
                {t(`${NS}.form.selectRoleFirst`)}
              </FieldHelperText>
            )}
            {error !== undefined && (
              <FieldErrorText>{error.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />
    )
  }

  const contractIdField = (
    name: 'loadFollowingContractId' | 'baseContractId',
    labelKey: string,
  ): React.ReactElement => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FieldRoot invalid={errors[name] !== undefined} required>
          <FieldLabel htmlFor={`${idPrefix}-${name}`}>
            {t(labelKey)}
            <FieldRequiredIndicator />
          </FieldLabel>
          <Input
            {...field}
            id={`${idPrefix}-${name}`}
            placeholder={t(`${NS}.form.contractIdPlaceholder`)}
          />
          {errors[name] !== undefined && (
            <FieldErrorText>{errors[name].message}</FieldErrorText>
          )}
        </FieldRoot>
      )}
    />
  )

  return (
    <Stack gap={4}>
      {isFormOptionsError && (
        <Stack gap={2}>
          <Alert
            description={t(`${NS}.form.optionsLoadFailed`)}
            status="error"
          />
          <HStack>
            <SecondaryButton onClick={onRetryFormOptions} size="sm">
              {t(`${NS}.form.retry`)}
            </SecondaryButton>
          </HStack>
        </Stack>
      )}
      {/* 供給地点特定番号: 新規は顧客管理の地点から選ぶ。 編集は変更不可の表示。 */}
      {mode === 'create' ? (
        <FieldRoot>
          <FieldLabel>{t(`${NS}.form.supplyPointArea`)}</FieldLabel>
          <DsSelect
            width="100%"
            aria-label={t(`${NS}.form.supplyPointArea`)}
            onChange={(v) => {
              const next = v ?? AREA_ALL
              setAreaFilter(next)
              // 絞り込みで今の選択が外れたら供給地点を空にする
              const current = getValues('supplyPointSpecificNumber')
              const stillVisible = options.supplyPoints.some(
                (p) =>
                  p.supplyPointSpecificNumber === current &&
                  (next === AREA_ALL || String(p.areaCode) === next),
              )
              if (current !== '' && !stillVisible) {
                setValue('supplyPointSpecificNumber', '', {
                  shouldDirty: true,
                })
              }
            }}
            options={[
              {
                value: AREA_ALL,
                label: t('page:adminOcctoMaster.toolbar.areaAll'),
              },
              ...areaOptions,
            ]}
            value={areaFilter}
          />
        </FieldRoot>
      ) : null}
      {mode === 'create' ? (
        <FieldRoot
          invalid={errors.supplyPointSpecificNumber !== undefined}
          required
        >
          <FieldLabel>
            {t(`${NS}.form.supplyPoint`)}
            <FieldRequiredIndicator />
          </FieldLabel>
          {isLoadingFormOptions ? (
            <Spinner />
          ) : (
            <FormSelect
              width="100%"
              aria-label={t(`${NS}.form.supplyPoint`)}
              control={control}
              name="supplyPointSpecificNumber"
              options={visibleSupplyPoints.map((p) => ({
                value: p.supplyPointSpecificNumber,
                label: t(`${NS}.form.supplyPointOption`, {
                  customer: p.customerName,
                  area: convertAreaLabel(p.areaCode),
                  voltage: voltageClassLabel(p.voltageClass),
                  number: p.supplyPointSpecificNumber,
                }),
              }))}
              placeholder={t(`${NS}.form.supplyPointPlaceholder`)}
            />
          )}
          {isFormOptionsError ? null : !isLoadingFormOptions &&
            options.supplyPoints.length === 0 ? (
            <FieldHelperText>
              {t(`${NS}.form.supplyPointNoCandidates`)}
            </FieldHelperText>
          ) : (
            <FieldHelperText>{t(`${NS}.form.supplyPointHint`)}</FieldHelperText>
          )}
          {errors.supplyPointSpecificNumber !== undefined && (
            <FieldErrorText>
              {errors.supplyPointSpecificNumber.message}
            </FieldErrorText>
          )}
        </FieldRoot>
      ) : (
        <Controller
          control={control}
          name="supplyPointSpecificNumber"
          render={({ field }) => (
            <FieldRoot>
              <FieldLabel htmlFor={`${idPrefix}-supplyPoint`}>
                {t(`${NS}.form.supplyPoint`)}
              </FieldLabel>
              <ImmutableInput
                {...field}
                id={`${idPrefix}-supplyPoint`}
                readOnly
              />
              <FieldHelperText>
                {customerName === undefined
                  ? t(`${NS}.noCustomerHint`)
                  : t(`${NS}.form.supplyPointImmutable`, {
                      customer: customerName,
                    })}
              </FieldHelperText>
            </FieldRoot>
          )}
        />
      )}

      {/* 自社の立場 */}
      <FieldRoot invalid={errors.selfRole !== undefined} required>
        <FieldLabel>
          {t(`${NS}.form.selfRole`)}
          <FieldRequiredIndicator />
        </FieldLabel>
        <HStack gap={2}>
          <ToggleButton
            onClick={() => {
              selectRole('loadFollowing')
            }}
            pressed={selfRole === 'loadFollowing'}
            size="sm"
          >
            {t(`${NS}.form.selfRoleLoadFollowing`)}
          </ToggleButton>
          <ToggleButton
            onClick={() => {
              selectRole('base')
            }}
            pressed={selfRole === 'base'}
            size="sm"
          >
            {t(`${NS}.form.selfRoleBase`)}
          </ToggleButton>
        </HStack>
        <FieldHelperText>{t(`${NS}.form.selfRoleHint`)}</FieldHelperText>
        {errors.selfRole !== undefined && (
          <FieldErrorText>{errors.selfRole.message}</FieldErrorText>
        )}
      </FieldRoot>

      {/* 負荷追随分供給者 */}
      <Text textStyle="captionStrong" color="Text.midEmphasis">
        {t(`${NS}.form.loadFollowingHeading`)}
      </Text>
      <SimpleGrid gap={3} templateColumns="2fr 1fr 1fr">
        {businessField('loadFollowing')}
        {contractIdField(
          'loadFollowingContractId',
          `${NS}.form.loadFollowingContractId`,
        )}
      </SimpleGrid>

      {/* ベース分供給者 */}
      <Text textStyle="captionStrong" color="Text.midEmphasis">
        {t(`${NS}.form.baseHeading`)}
      </Text>
      <SimpleGrid gap={3} templateColumns="2fr 1fr 1fr">
        {businessField('base')}
        {contractIdField('baseContractId', `${NS}.form.baseContractId`)}
        <Controller
          control={control}
          name="basePriority"
          render={({ field }) => (
            <FieldRoot invalid={errors.basePriority !== undefined}>
              <FieldLabel htmlFor={`${idPrefix}-basePriority`}>
                {t(`${NS}.form.priority`)}
              </FieldLabel>
              <Input
                {...field}
                id={`${idPrefix}-basePriority`}
                inputMode="numeric"
                placeholder={t(`${NS}.form.priorityPlaceholder`)}
              />
              {errors.basePriority !== undefined && (
                <FieldErrorText>{errors.basePriority.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      </SimpleGrid>

      {/* 供給形態 */}
      <FieldRoot invalid={errors.supplyForm !== undefined} required>
        <FieldLabel>
          {t(`${NS}.form.supplyForm`)}
          <FieldRequiredIndicator />
        </FieldLabel>
        <HStack gap={2}>
          {SUPPLY_FORMS.map((form) => (
            <ToggleButton
              key={form}
              onClick={() => {
                // 変動型へ切り替えた時は 通告値を出す側 を未選択から始める
                if (form === SplitSupplyForm.VARIABLE && supplyForm !== form) {
                  setValue('noticeBy', '', { shouldDirty: true })
                }
                setValue('supplyForm', form, { shouldDirty: true })
              }}
              pressed={supplyForm === form}
              size="sm"
            >
              {supplyFormLabel(t, form)}
            </ToggleButton>
          ))}
        </HStack>
        <FieldHelperText>{t(`${NS}.form.supplyFormHint`)}</FieldHelperText>
        {errors.supplyForm !== undefined && (
          <FieldErrorText>{errors.supplyForm.message}</FieldErrorText>
        )}
      </FieldRoot>

      {/* 通告値を出す側 (変動型だけ)。 立場では決まらず、 分割供給の申込み時に届け出た側を選ぶ */}
      {supplyForm === SplitSupplyForm.VARIABLE && (
        <FieldRoot invalid={errors.noticeBy !== undefined} required>
          <FieldLabel>
            {t(`${NS}.form.noticeBy`)}
            <FieldRequiredIndicator />
          </FieldLabel>
          <HStack gap={2}>
            {NOTICE_BY_CHOICES.map((choice) => (
              <ToggleButton
                key={choice}
                disabled={selfRole === ''}
                onClick={() => {
                  setValue('noticeBy', choice, { shouldDirty: true })
                }}
                pressed={noticeBy === choice}
                size="sm"
              >
                {t(
                  `${NS}.form.${choice === 'self' ? 'noticeBySelf' : 'noticeByOther'}`,
                )}
              </ToggleButton>
            ))}
          </HStack>
          <FieldHelperText>{t(`${NS}.form.noticeByHint`)}</FieldHelperText>
          {selfRole === '' && (
            <FieldHelperText>{t(`${NS}.form.selectRoleFirst`)}</FieldHelperText>
          )}
          {errors.noticeBy !== undefined && (
            <FieldErrorText>{errors.noticeBy.message}</FieldErrorText>
          )}
        </FieldRoot>
      )}

      {/* 固定型のベース電力量 = ベース分供給者の契約電力 ÷ 2 */}
      {supplyForm === SplitSupplyForm.FIXED && (
        <Controller
          control={control}
          name="baseContractKw"
          render={({ field }) => (
            <FieldRoot invalid={errors.baseContractKw !== undefined} required>
              <FieldLabel htmlFor={`${idPrefix}-baseContractKw`}>
                {t(`${NS}.form.baseContractKw`)}
                <FieldRequiredIndicator />
              </FieldLabel>
              <Input
                {...field}
                width="160px"
                id={`${idPrefix}-baseContractKw`}
                inputMode="numeric"
              />
              <FieldHelperText>
                {t(`${NS}.form.baseContractKwHint`)}
              </FieldHelperText>
              {errors.baseContractKw !== undefined && (
                <FieldErrorText>{errors.baseContractKw.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      )}

      {supplyForm === SplitSupplyForm.TIME_SPLIT && (
        <Stack gap={2}>
          <Text textStyle="label">{t(`${NS}.form.ownSlotHeading`)}</Text>
          <SimpleGrid gap={3} columns={2}>
            <FieldRoot required>
              <FieldLabel>
                {t(`${NS}.form.ownSlotFrom`)}
                <FieldRequiredIndicator />
              </FieldLabel>
              <FormSelect
                aria-label={t(`${NS}.form.ownSlotFrom`)}
                control={control}
                name="ownSlotFrom"
                onValueChange={(from) => {
                  // 開始を変えて終了が前になれば終了を空にする
                  const to = getValues('ownSlotTo')
                  if (
                    from !== undefined &&
                    to !== '' &&
                    Number(to) < Number(from)
                  ) {
                    setValue('ownSlotTo', '', { shouldDirty: true })
                  }
                }}
                options={SLOT_START_OPTIONS}
                placeholder={t(`${NS}.form.slotPlaceholder`)}
              />
            </FieldRoot>
            <FieldRoot invalid={errors.ownSlotTo !== undefined} required>
              <FieldLabel>
                {t(`${NS}.form.ownSlotTo`)}
                <FieldRequiredIndicator />
              </FieldLabel>
              <FormSelect
                aria-label={t(`${NS}.form.ownSlotTo`)}
                control={control}
                name="ownSlotTo"
                options={SLOT_END_OPTIONS.filter(
                  (o) => slotFrom === '' || Number(o.value) >= Number(slotFrom),
                )}
                placeholder={t(`${NS}.form.slotPlaceholder`)}
              />
              {errors.ownSlotTo !== undefined && (
                <FieldErrorText>{errors.ownSlotTo.message}</FieldErrorText>
              )}
            </FieldRoot>
          </SimpleGrid>
        </Stack>
      )}

      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel htmlFor={`${idPrefix}-note`}>
              {t(`${NS}.form.note`)}
            </FieldLabel>
            <Textarea {...field} id={`${idPrefix}-note`} />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      <BasePatternsSection
        onChange={(next) => {
          setValue('basePatterns', next, { shouldDirty: true })
        }}
        patterns={patterns}
      />
      {errors.basePatterns?.message !== undefined && (
        <FieldRoot invalid>
          <FieldErrorText>{errors.basePatterns.message}</FieldErrorText>
        </FieldRoot>
      )}
    </Stack>
  )
}
